import { useState } from 'react';

import { Icon } from '../../../Icon';
import { useIsDesktop } from '../../../hooks';
import { Box } from '../../../Box';
import { ButtonGroup } from '../../../ButtonGroup';
import { Button } from '../../../Button';
import { useStyles } from '../Preview.styles';

import { doDownload } from './download/download';
import { DownloadButton } from './DownloadButton';

interface PreviewButtonsProps {
  pdf?: boolean;
  withCollapsableFooter?: boolean;
  onClickDownload?: () => Promise<
    Blob | { file: Promise<Blob>; filename?: string }
  >;
  onClickPrint?: () => void;
  clickable?: boolean;
}

export const PreviewButtons = ({
  pdf,
  withCollapsableFooter,
  onClickDownload,
  onClickPrint,
  clickable,
}: PreviewButtonsProps) => {
  const isDesktop = useIsDesktop();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const { classes } = useStyles({});

  const handleDownloadClick = async () => {
    if (pdf) {
      setIsLoading(true);
      setError(null);
    }

    if (!onClickDownload) {
      return;
    }

    const handlerRes = await onClickDownload();
    if (typeof handlerRes === 'object' && 'file' in handlerRes) {
      doDownload(handlerRes.file, handlerRes.filename || 'document').then(
        () => {
          if (pdf) {
            setIsLoading(false);
          }
        },
        (e) => {
          if (pdf) {
            setError(e);
            setIsLoading(false);
          }
        },
      );
    } else {
      doDownload(handlerRes, 'documents.zip').then(
        () => {
          if (pdf) {
            setIsLoading(false);
          }
        },
        (e) => {
          if (pdf) {
            setError(e);
            setIsLoading(false);
          }
        },
      );
    }
  };

  const handlePrintClick = async () => {
    if (!pdf || !onClickPrint) return;

    setIsPrinting(true);

    try {
      await onClickPrint();
    } finally {
      setIsPrinting(false);
    }
  };

  if (
    ([
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)) &&
    navigator.userAgent.match('CriOS')
  ) {
    return null;
  }

  const buttons = [
    ...(isDesktop && onClickPrint
      ? [
          <Button
            key="print"
            icon={<Icon name="printer_outline" size="24" />}
            onClick={handlePrintClick}
            loading={isPrinting}
            disabled={isPrinting}
          />,
        ]
      : []),
    <Button
      key="download"
      icon={<Icon name="download_outline" size="24" />}
      loading={isLoading}
      disabled={isLoading}
      onClick={handleDownloadClick}
    />,
  ];

  const buttonGroup =
    pdf && !error ? (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p="20"
        className={classes.previewButtons}
      >
        <ButtonGroup
          size={isDesktop ? 'large' : 'small'}
          orientation={isDesktop ? 'horizontal' : 'vertical'}
          separated={!isDesktop}
          onClick={(event) => event.stopPropagation()}
        >
          {buttons}
        </ButtonGroup>
      </Box>
    ) : null;

  return withCollapsableFooter ? (
    <DownloadButton onClick={handleDownloadClick} clickable={clickable} />
  ) : (
    buttonGroup
  );
};
